// components/hauler/JobsMap.js
//
// Hauler Jobs map contract:
// - The map is a discovery surface for AVAILABLE opportunities only.
// - Map markers are count circles only: native cluster counts or "1".
// - No prices, payout pills, distance text, claimed markers, or claimed dots.
// - Claimed/active jobs remain in the list below the map.
// - Available coordinates still flow through the privacy helper, so unrelated
//   Haulers see approximate area positions while owners/admins can see exact.

import React, { useEffect, useRef, useState } from "react";
import { MAPBOX_TOKEN } from "../../constants";
import { getAvailableJobDisplayCoordinate } from "../../utils/haulerMapPrivacy";

const DEFAULT_CENTER = [-112.002, 41.109];
const MAPBOX_VERSION = "v3.11.0";

const HAULER_MAP_STYLE =
  process.env.REACT_APP_MAPBOX_HAULER_STYLE ||
  "mapbox://styles/trashdash/cmohmfrbc002601ss5in5as4e";

const SOURCE_ID = "available-jobs";
const HEATMAP_LAYER_ID = "opportunity-zones";
const CLUSTER_LAYER_ID = "available-clusters";
const CLUSTER_COUNT_LAYER_ID = "available-cluster-counts";
const SINGLE_LAYER_ID = "available-single-counts";
const SINGLE_COUNT_LAYER_ID = "available-single-count-labels";

const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS_PX = 60;

let _mapboxLoaderPromise = null;
function loadMapbox() {
  if (typeof window === "undefined") return Promise.reject(new Error("no_window"));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (_mapboxLoaderPromise) return _mapboxLoaderPromise;
  _mapboxLoaderPromise = new Promise((resolve, reject) => {
    const cssId = "mapbox-gl-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.css`;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_VERSION}/mapbox-gl.js`;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error("mapbox_script_failed"));
    document.head.appendChild(script);
  });
  return _mapboxLoaderPromise;
}

function buildFeatures(jobs, viewerUserId, viewerIsAdmin) {
  if (!jobs || !jobs.length) return [];

  const opts = { isAdmin: !!viewerIsAdmin };
  const features = [];
  for (const job of jobs) {
    const display = getAvailableJobDisplayCoordinate(job, viewerUserId, opts);
    if (!Number.isFinite(display.lat) || !Number.isFinite(display.lng)) continue;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [display.lng, display.lat] },
      properties: { id: job.id },
    });
  }
  return features;
}

const HEATMAP_PAINT = {
  "heatmap-weight": [
    "case",
    ["has", "point_count"], ["min", ["get", "point_count"], 10],
    1,
  ],
  "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 10, 0.7, 14, 1.6],
  "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 10, 35, 14, 85],
  "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 13, 0.72, 15, 0],
  "heatmap-color": [
    "interpolate", ["linear"], ["heatmap-density"],
    0, "rgba(0,0,0,0)",
    0.2, "rgba(0,140,255,0.22)",
    0.45, "rgba(0,255,150,0.36)",
    0.7, "rgba(150,80,255,0.48)",
    1, "rgba(255,130,35,0.58)",
  ],
};

const COUNT_CIRCLE_PAINT = {
  "circle-color": "#0A1628",
  "circle-stroke-color": "#3D90D9",
  "circle-stroke-width": 2,
  "circle-radius": ["step", ["coalesce", ["get", "point_count"], 1], 24, 5, 30, 10, 36],
};

const CLUSTER_COUNT_LAYOUT = {
  "text-field": ["get", "point_count_abbreviated"],
  "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
  "text-size": 14,
};

const SINGLE_COUNT_LAYOUT = {
  "text-field": "1",
  "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
  "text-size": 14,
};

const COUNT_TEXT_PAINT = {
  "text-color": "#FFFFFF",
};

export default function JobsMap({
  jobs = [],
  height = 240,
  onError,
  onSelectJob,
  viewerUserId = null,
  viewerIsAdmin = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const jobsByIdRef = useRef(new Map());
  const [unavailable, setUnavailable] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const idsKey = jobs.map(j => j.id).join(",");

  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setUnavailable(true);
      onError && onError("missing_token");
      return;
    }
    if (!containerRef.current) return;

    let cancelled = false;
    loadMapbox()
      .then((mapboxgl) => {
        if (cancelled || !containerRef.current) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: HAULER_MAP_STYLE,
          center: DEFAULT_CENTER,
          zoom: 13,
          attributionControl: false,
        });
        mapRef.current = map;
        map.on("load", () => {
          if (cancelled) return;
          setMapReady(true);
        });
      })
      .catch((err) => {
        setUnavailable(true);
        onError && onError(err?.message || "mapbox_load_failed");
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
      jobsByIdRef.current = new Map();
    };
  }, []);// eslint-disable-line

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (typeof window === "undefined" || !window.mapboxgl) return;

    const jobsById = new Map();
    jobs.forEach(j => { if (j && j.id != null) jobsById.set(j.id, j); });
    jobsByIdRef.current = jobsById;

    const features = buildFeatures(jobs, viewerUserId, viewerIsAdmin);
    const data = { type: "FeatureCollection", features };

    const source = map.getSource(SOURCE_ID);
    if (source && typeof source.setData === "function") {
      source.setData(data);
    } else {
      try {
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data,
          cluster: true,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
          clusterRadius: CLUSTER_RADIUS_PX,
        });
      } catch (_) {}
    }

    addLayers(map);

    if (features.length === 1) {
      try { map.setCenter(features[0].geometry.coordinates); map.setZoom(15); } catch (_) {}
    } else if (features.length > 1) {
      const bounds = new window.mapboxgl.LngLatBounds();
      features.forEach(f => bounds.extend(f.geometry.coordinates));
      try { map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 15 }); } catch (_) {}
    } else {
      try { map.setCenter(DEFAULT_CENTER); map.setZoom(13); } catch (_) {}
    }
  }, [mapReady, idsKey, viewerUserId, viewerIsAdmin]);// eslint-disable-line

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const onClusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER_ID, CLUSTER_COUNT_LAYER_ID] });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      const source = map.getSource(SOURCE_ID);
      if (!source || typeof source.getClusterExpansionZoom !== "function") return;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        try { map.easeTo({ center: features[0].geometry.coordinates, zoom }); } catch (_) {}
      });
    };

    const onSingleClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [SINGLE_LAYER_ID, SINGLE_COUNT_LAYER_ID] });
      if (!features.length) return;
      const id = features[0].properties && features[0].properties.id;
      const job = jobsByIdRef.current.get(id);
      if (job && onSelectJob) onSelectJob(job);
    };

    const setPointer = () => { try { map.getCanvas().style.cursor = "pointer"; } catch (_) {} };
    const clearPointer = () => { try { map.getCanvas().style.cursor = ""; } catch (_) {} };

    const interactiveLayers = [CLUSTER_LAYER_ID, CLUSTER_COUNT_LAYER_ID, SINGLE_LAYER_ID, SINGLE_COUNT_LAYER_ID];
    map.on("click", CLUSTER_LAYER_ID, onClusterClick);
    map.on("click", CLUSTER_COUNT_LAYER_ID, onClusterClick);
    map.on("click", SINGLE_LAYER_ID, onSingleClick);
    map.on("click", SINGLE_COUNT_LAYER_ID, onSingleClick);
    interactiveLayers.forEach((layer) => {
      map.on("mouseenter", layer, setPointer);
      map.on("mouseleave", layer, clearPointer);
    });

    return () => {
      try { map.off("click", CLUSTER_LAYER_ID, onClusterClick); } catch (_) {}
      try { map.off("click", CLUSTER_COUNT_LAYER_ID, onClusterClick); } catch (_) {}
      try { map.off("click", SINGLE_LAYER_ID, onSingleClick); } catch (_) {}
      try { map.off("click", SINGLE_COUNT_LAYER_ID, onSingleClick); } catch (_) {}
      interactiveLayers.forEach((layer) => {
        try { map.off("mouseenter", layer, setPointer); } catch (_) {}
        try { map.off("mouseleave", layer, clearPointer); } catch (_) {}
      });
    };
  }, [mapReady, onSelectJob]);

  if (unavailable) return null;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height,
        marginBottom: 14,
        borderRadius: 14,
        overflow: "hidden",
        background: "var(--hl-bg)",
        border: "1px solid var(--hl-border)",
      }}
    />
  );
}

function addLayers(map) {
  if (!map.getLayer(HEATMAP_LAYER_ID)) {
    try {
      map.addLayer({
        id: HEATMAP_LAYER_ID,
        type: "heatmap",
        source: SOURCE_ID,
        maxzoom: 15,
        paint: HEATMAP_PAINT,
        slot: "top",
      });
    } catch (_) {
      try {
        map.addLayer({
          id: HEATMAP_LAYER_ID,
          type: "heatmap",
          source: SOURCE_ID,
          maxzoom: 15,
          paint: HEATMAP_PAINT,
        });
      } catch (_) {}
    }
  }

  if (!map.getLayer(CLUSTER_LAYER_ID)) {
    try {
      map.addLayer({
        id: CLUSTER_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        paint: COUNT_CIRCLE_PAINT,
      });
    } catch (_) {}
  }

  if (!map.getLayer(CLUSTER_COUNT_LAYER_ID)) {
    try {
      map.addLayer({
        id: CLUSTER_COUNT_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: CLUSTER_COUNT_LAYOUT,
        paint: COUNT_TEXT_PAINT,
      });
    } catch (_) {}
  }

  if (!map.getLayer(SINGLE_LAYER_ID)) {
    try {
      map.addLayer({
        id: SINGLE_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        paint: COUNT_CIRCLE_PAINT,
      });
    } catch (_) {}
  }

  if (!map.getLayer(SINGLE_COUNT_LAYER_ID)) {
    try {
      map.addLayer({
        id: SINGLE_COUNT_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        layout: SINGLE_COUNT_LAYOUT,
        paint: COUNT_TEXT_PAINT,
      });
    } catch (_) {}
  }
}
